/**
 * Utilities for cross-instance aggregation in the local explorer.
 *
 * When multiple Miniflare instances run with a shared dev registry,
 * any one instance can aggregate data from all instances.
 */

import { env } from "cloudflare:workers";
import { CorePaths } from "../core";
import type { WorkerRegistry } from "../../shared/dev-registry-types";
import type { AppContext } from "./common";

const EXPLORER_API_PATH = `${CorePaths.EXPLORER}/api`;

/**
 * Header that indicates a request should not trigger further aggregation.
 * Used to prevent infinite recursion when instance A fetches from instance B.
 */
export const NO_AGGREGATE_HEADER = "X-Miniflare-Explorer-No-Aggregate";

/**
 * Get the unique base URLs of peer instances from the dev registry,
 * excluding the current instance (identified by worker names).
 */
function getPeerDebugPortAddresses(
	registry: WorkerRegistry,
	selfWorkerNames: string[],
	selfInstanceId: string | null
): string[] {
	const selfSet = new Set(selfWorkerNames);
	const addresses = Object.entries(registry)
		.filter(
			([name, definition]) =>
				!selfSet.has(name) && definition.instanceId !== selfInstanceId
		)
		.map(([, def]) => def.debugPortAddress)
		.filter((addr): addr is string => typeof addr === "string");
	// A single Miniflare process with multiple workers registers multiple
	// entries in the registry, all sharing the same host:port. We deduplicate
	// to avoid fetching from the same peer multiple times.
	return [...new Set(addresses)];
}

function getSharedStoragePeerDebugPortAddresses(
	registry: WorkerRegistry,
	selfInstanceId: string | null
): string[] {
	if (selfInstanceId === null) {
		return [];
	}
	const selfStorageScope = Object.values(registry).find(
		(definition) =>
			definition.instanceId === selfInstanceId &&
			definition.storageScope !== undefined
	)?.storageScope;
	if (selfStorageScope === undefined) {
		return [];
	}

	const addresses = Object.values(registry)
		.filter(
			(definition) =>
				definition.instanceId !== selfInstanceId &&
				definition.storageScope === selfStorageScope
		)
		.map((definition) => definition.debugPortAddress);
	return [...new Set(addresses)];
}

interface PeerAggregationOptions {
	sharedStorageOnly?: boolean;
}

export async function getPeerUrlsIfAggregating(
	c: AppContext,
	options: PeerAggregationOptions = {}
): Promise<string[]> {
	if (c.req.raw.headers.has(NO_AGGREGATE_HEADER)) {
		return [];
	}
	const loopback = c.env.MINIFLARE_LOOPBACK;
	const workerNames = c.env.LOCAL_EXPLORER_WORKER_NAMES;
	const response = await loopback.fetch("http://localhost/core/dev-registry");
	const registry = (await response.json()) as WorkerRegistry;
	const selfInstanceId = response.headers.get(
		"X-Miniflare-Dev-Registry-Instance-Id"
	);
	if (options.sharedStorageOnly) {
		return getSharedStoragePeerDebugPortAddresses(registry, selfInstanceId);
	}
	return getPeerDebugPortAddresses(registry, workerNames, selfInstanceId);
}

export function getPeerEntrypoint(
	peerDebugPortAddress: string,
	service: string
): Fetcher {
	const client = (env as AppContext["env"]).DEV_REGISTRY_DEBUG_PORT.connect(
		peerDebugPortAddress
	);
	return client.getEntrypoint(service);
}

/**
 * Fetch data from a peer instance's explorer API.
 * Returns null on any error (silent omission policy).
 *
 * @param peerDebugPortAddress - Debug port address of the peer instance (e.g., "127.0.0.1:12345")
 * @param apiPath - API path relative to the explorer API base (e.g., "/d1/database")
 * @param init - Optional fetch init options
 */
export async function fetchFromPeer(
	peerDebugPortAddress: string,
	apiPath: string,
	init?: RequestInit
): Promise<Response | null> {
	try {
		const fetcher = getPeerEntrypoint(peerDebugPortAddress, "core:entry");
		const url = new URL(`http://localhost${EXPLORER_API_PATH}${apiPath}`);
		const response = await fetcher.fetch(url.toString(), {
			...init,
			headers: {
				...(init?.headers as Record<string, string> | undefined),
				[NO_AGGREGATE_HEADER]: "true",
				Host: "localhost",
			},
		});
		return new Response(response.body, response);
	} catch {
		return null;
	}
}

interface AggregateListResultsOptions<T> {
	getKey?: (result: T) => string | undefined;
	resultKey?: string;
	sharedStorageOnly?: boolean;
}

/**
 * Aggregate list results from local data and peer instances.
 *
 * @param c - Hono app context
 * @param localResults - Results from the local instance
 * @param apiPath - API path relative to the explorer API base
 * @param options - Aggregation scope and response-shape options
 */
export async function aggregateListResults<T>(
	c: AppContext,
	localResults: T[],
	apiPath: string,
	options: AggregateListResultsOptions<T> = {}
): Promise<T[]> {
	const peerUrls = await getPeerUrlsIfAggregating(c, options);
	const peerResults = await Promise.all(
		peerUrls.map(async (url) => {
			const response = await fetchFromPeer(url, apiPath);
			if (!response?.ok) {
				return [];
			}
			try {
				const data = (await response.json()) as {
					result: T[] | { [key: string]: T[] };
				};
				if (Array.isArray(data.result)) {
					return data.result;
				}
				if (options.resultKey) {
					return data.result[options.resultKey] ?? [];
				}
				throw new Error("unreachable");
			} catch {
				return [];
			}
		})
	);

	const results = [...localResults, ...peerResults.flat()];
	const getKey = options.getKey;
	if (getKey === undefined) {
		return results;
	}

	const seen = new Set<string>();
	return results.filter((result) => {
		const key = getKey(result);
		if (key === undefined) {
			return true;
		}
		if (seen.has(key)) {
			return false;
		}
		seen.add(key);
		return true;
	});
}
